import {
  ProductAllowedCombinationRequest,
  ProductAllowedCombinationSelectedOption,
  ProductConfigurableOptionsClient,
  type IProductConfigurableOptionsResponse
} from '../listing/api/client';

/**
 * Inlined rather than imported from the listing engine: that module exports React
 * hooks, and importing it drags the React vendor chunk into a vanilla module.
 */
function withAbortSignal(signal: AbortSignal): {
  fetch(url: RequestInfo, init?: RequestInit): Promise<Response>;
} {
  return { fetch: (url, init) => window.fetch(url, { ...init, signal }) };
}

/**
 * Wraps the generated `ProductConfigurableOptionsClient` and normalizes its
 * all-optional NSwag types. The response is narrowed rather than annotated:
 * unreachable values are absent, not flagged.
 */

export interface SelectedOptionInput {
  attributeCode: string;
  valueIndex: number;
}

export interface AllowedValue {
  valueIndex: number;
  uid: string;
  label?: string;
  isSelected: boolean;
}

export interface AllowedOption {
  attributeCode: string;
  label?: string;
  values: AllowedValue[];
}

export interface ConfigurableOptionsResult {
  currency: string;
  options: AllowedOption[];
  lowestFinalPrice?: number;
  isComplete: boolean;
  matchedSku?: string;
}

function normalize(response: IProductConfigurableOptionsResponse): ConfigurableOptionsResult {
  const options = (response.options ?? []).flatMap<AllowedOption>(option => {
    if (!option.attributeCode) {
      return [];
    }

    return [
      {
        attributeCode: option.attributeCode,
        label: option.label ?? undefined,
        values: (option.values ?? []).flatMap<AllowedValue>(value =>
          value.uid !== undefined && value.valueIndex !== undefined
            ? [
                {
                  uid: value.uid,
                  valueIndex: value.valueIndex,
                  label: value.label ?? undefined,
                  isSelected: value.isSelected ?? false
                }
              ]
            : []
        )
      }
    ];
  });

  return {
    currency: response.currency ?? '',
    options,
    lowestFinalPrice: response.lowestFinalPrice ?? undefined,
    isComplete: response.isComplete ?? false,
    matchedSku: response.matchedSku ?? undefined
  };
}

export async function fetchConfiguration(
  apiBaseUrl: string,
  productGuid: string,
  selected: SelectedOptionInput[],
  signal: AbortSignal
): Promise<ConfigurableOptionsResult> {
  const client = new ProductConfigurableOptionsClient(apiBaseUrl, withAbortSignal(signal));

  const request = new ProductAllowedCombinationRequest({
    productGuid,
    selected: selected.map(
      option =>
        new ProductAllowedCombinationSelectedOption({
          attributeCode: option.attributeCode,
          valueIndex: option.valueIndex
        })
    )
  });

  return normalize(await client.configuration(request));
}
